import { useEffect, useMemo, useState } from "react";
import { PLAY_CHIPS_NOTICE, RACE_STAKE_CHOICES, formatRaceOdds, racePayout, type RaceBet, type RaceHorse, type RacingPrivate, type RacingState } from "@townsy/contracts";
import { RACE_BETTING_ZONE_TEXT } from "@townsy/world";
import { SignUpToPlay } from "../SignUpToPlay";
import type { VenueHudProps } from "../types";
import { SADDLE_CLOTH, asRacingPrivate, asRacingState, betBlocker, headline, liveOrder, maxStake, ordinal, secondsLeft, silkCss } from "./model";
import "./raceHud.css";

/**
 * Townsy Downs overlay (ADR-0016): the phase and countdown, the race card (number, silks, name, odds; pick a horse),
 * the stake, Place bet, your open bets with Cancel, the live order while they run, your result, Sit / Stand up near
 * the grandstand, and the play-chips notice. A guest (ADR-0048: betting needs an account) sees the same race card and
 * race and can sit in the stands; "Sign up (18+) to play" takes the place of the stake and Place bet. It only displays
 * the server's state and sends intents.
 */
export function RaceHud(props: VenueHudProps) {
  const state = asRacingState(props.shared);
  const mine = asRacingPrivate(props.mine);
  const receivedAt = useMemo(() => Date.now(), [props.shared]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, []);
  return <RaceHudView {...props} state={state} mine={mine} seconds={state ? secondsLeft(state, receivedAt, Math.max(now, receivedAt)) : 0} />;
}

export interface RaceHudViewProps extends VenueHudProps {
  state: RacingState | null;
  mine: RacingPrivate | null;
  /** Seconds left in the betting or results phase. */
  seconds: number;
}

/** The overlay for a given state (separate from the timers so it renders the same on the server in tests). */
export function RaceHudView({ state, mine, chips, chanceGamesEnabled, guest = false, send, seconds }: RaceHudViewProps) {
  const [horse, setHorse] = useState<number | null>(null);
  const [stake, setStake] = useState<number>(5);
  if (!state) {
    return (
      <div className="venue-hud-body race-hud" data-testid="race-hud">
        <p style={{ margin: 0 }}>Connecting to the race track...</p>
        <p className="small muted race-fineprint">{PLAY_CHIPS_NOTICE}</p>
      </div>
    );
  }
  const head = headline(state, seconds);
  const order = state.phase === "betting" ? [] : liveOrder(state);
  const place = (n: number) => order.indexOf(n) + 1;
  const max = maxStake(chips, mine);
  // A guest while betting is on (ADR-0048): "Sign up (18+) to play" instead of the stake and Place bet.
  const signUp = guest && chanceGamesEnabled;
  const blocker = betBlocker({ state, mine, chips, chanceGamesEnabled, guest, horse, stake, zoneText: RACE_BETTING_ZONE_TEXT });
  const pickable = chanceGamesEnabled && !signUp && state.phase === "betting" && state.bettingOpen;
  const seated = mine?.seat !== null && mine?.seat !== undefined;
  const myResult = mine?.last && state.result && mine.last.raceId === state.result.raceId ? mine.last : null;
  const byN = (n: number): RaceHorse | undefined => state.horses[n - 1];

  return (
    <div className="venue-hud-body race-hud" data-testid="race-hud" data-phase={state.phase}>
      <div className="race-status">
        {/* Announced on phase changes only; the countdown beside it is not a live region. */}
        <strong role="status" aria-live="polite" data-testid="race-headline">
          {head.title}
        </strong>
        {head.detail ? (
          <span className="race-detail" data-testid="race-countdown">
            {head.detail}
          </span>
        ) : null}
      </div>

      {myResult ? (
        <p className={`notice small race-result ${myResult.won > 0 ? "notice-success" : ""}`} data-testid="race-my-result">
          {myResult.won > 0 ? `You won ${myResult.won} play chips!` : "No win this time."}
          {` You bet ${myResult.staked} on this race.`}
        </p>
      ) : null}

      <div className="race-card" role="group" aria-label={pickable ? "Race card: pick the horse you think will win" : "Race card"}>
        {state.horses.map((h) => {
          const pos = state.phase === "betting" ? 0 : place(h.n);
          const cloth = SADDLE_CLOTH[h.n] ?? { cloth: "#FFFFFF", text: "#1B1F2A" };
          const won = state.result?.winner.n === h.n;
          return (
            <button
              key={h.n}
              type="button"
              className={`race-horse${won ? " race-horse-won" : ""}`}
              aria-pressed={pickable ? horse === h.n : undefined}
              disabled={!pickable}
              onClick={() => setHorse(horse === h.n ? null : h.n)}
              data-testid={`race-horse-${h.n}`}
              aria-label={`Number ${h.n}, ${h.name}, odds ${formatRaceOdds(h.odds)}${pos ? `, ${ordinal(pos)}` : ""}`}
            >
              <span className="race-num" style={{ background: cloth.cloth, color: cloth.text }} aria-hidden="true">
                {h.n}
              </span>
              <span className="race-silk" style={{ background: silkCss(h.silk) }} aria-hidden="true" />
              <span className="race-name">{h.name}</span>
              <span className="race-odds">{pos ? ordinal(pos) : formatRaceOdds(h.odds)}</span>
            </button>
          );
        })}
      </div>

      <div className="race-betrow">
        {signUp ? (
          <SignUpToPlay watch="Guests can watch the races and sit in the stands." />
        ) : (
          <>
            <div className="segmented race-stakes" role="group" aria-label="Stake in play chips">
              {RACE_STAKE_CHOICES.map((c) => (
                <button key={c} type="button" className="btn btn-sm" aria-pressed={stake === c} disabled={!pickable || c > max} onClick={() => setStake(c)} data-testid={`race-stake-${c}`}>
                  {c}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={blocker !== ""}
              aria-describedby={blocker ? "race-bet-hint" : undefined}
              onClick={() => {
                if (horse !== null && send({ type: "bet", horse, stake })) setHorse(null);
              }}
              data-testid="race-place-bet"
            >
              {horse !== null && pickable ? `Bet ${stake} on #${horse}` : "Place bet"}
            </button>
          </>
        )}
        {seated ? (
          <button type="button" className="btn btn-sm" onClick={() => send({ type: "stand" })} data-testid="race-stand">
            Stand up
          </button>
        ) : mine?.nearStands ? (
          <button type="button" className="btn btn-sm" onClick={() => send({ type: "sit" })} data-testid="race-sit">
            Sit in the stands
          </button>
        ) : (
          <span className="small muted">Walk up to the grandstand to sit and watch.</span>
        )}
      </div>
      {blocker && !signUp ? (
        <p id="race-bet-hint" className="small muted race-hint" data-testid="race-bet-hint">
          {blocker}
        </p>
      ) : null}

      {mine && mine.bets.length > 0 ? (
        <ul className="race-bets" aria-label="Your bets on this race" data-testid="race-my-bets">
          {mine.bets.map((b: RaceBet) => (
            <li key={b.id} data-testid="race-bet" title={`${byN(b.horse)?.name ?? ""}: ${b.stake} play chips at ${formatRaceOdds(b.odds)}`}>
              <span>
                <strong>#{b.horse}:</strong> {b.stake}, pays {racePayout(b.stake, b.odds)}
              </span>
              {state.phase === "betting" ? (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => send({ type: "cancel", betId: b.id })} aria-label={`Cancel your bet of ${b.stake} on number ${b.horse}`}>
                  Cancel
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      <details className="small muted race-fineprint">
        <summary>Play chips: just for fun, no value</summary>
        Win bets only: a winning bet pays your stake times the odds, plus your stake back. If you leave, your open bets still run, and a win is added to your play chips. {PLAY_CHIPS_NOTICE}
      </details>
    </div>
  );
}
